import type { Theme } from '@emotion/react';
import type { Appearance, StripeElementsOptions } from '@stripe/stripe-js';

export const getStripeAppearance = (theme: Theme): Appearance => {
    return {
        rules: {
            '.Checkbox': {
                color: theme.isLightColorScheme ? theme.colors.gray[500] : theme.colors.white,
                fontSize: '14px'
            },
            '.Input:focus': {
                borderColor: theme.colors.brand.primary,
                borderWidth: '2px',
                boxShadow: 'none'
            },
            '.Label': {
                color: theme.isLightColorScheme ? theme.colors.gray[500] : theme.colors.white,
                fontSize: '14px'
            }
        },
        theme: theme.isLightColorScheme ? 'stripe' : 'night',
        variables: {
            colorPrimary: theme.colors.brand.primary,
            fontFamily: 'open-sans, sans-serif'
        }
    };
};

export const getStripeFonts = (staticUrl: string): StripeElementsOptions['fonts'] => [
    {
        family: 'open-sans',
        src: `url(${staticUrl}/common/font/OpenSans-Regular.woff)`,
        weight: '400'
    },
    {
        family: 'open-sans',
        src: `url(${staticUrl}/common/font/OpenSans-SemiBold.woff)`,
        weight: '600'
    },
    {
        family: 'open-sans',
        src: `url(${staticUrl}/common/font/OpenSans-Bold.woff)`,
        weight: '700'
    }
];

export const getTimeZoneAbbreviation = (timezone: string) => {
    if (!timezone) {
        return '';
    }

    const timeZoneFormatter = new Intl.DateTimeFormat([], {
        timeZone: timezone,
        timeZoneName: 'short'
    });
    // Using new Date() to avoid browser timezone interference. If we want to use a fixed date, we can use new Date(Date.UTC(2023, 0, 1)) and this will always return the same result regardless of it being daylight savings or not.
    const parts = timeZoneFormatter.formatToParts(new Date());
    return parts.find((x) => x.type === 'timeZoneName')?.value;
};

// Formats TZ like -5:00 America/New_York
export const getTimeZoneName = (timezone: string) => {
    if (!timezone) {
        return '';
    }

    return Intl.DateTimeFormat([], { timeZone: timezone })
        .resolvedOptions()
        .timeZone.replace('_', ' ')
        .replace('/', ', ');
};

export const formatTimezone = (timezone: string) => {
    // Create a date in UTC to avoid browser timezone interference
    const utcDate = new Date(Date.UTC(2023, 0, 1)); // Using a fixed date to ensure consistent offset
    const formatter = new Intl.DateTimeFormat([], {
        timeZone: timezone,
        timeZoneName: 'longOffset'
    });
    const parts = formatter.formatToParts(utcDate);
    const offset = parts.find((part) => part.type === 'timeZoneName')?.value || '';
    return `(${offset}) ${getTimeZoneName(timezone)}`;
};

export const convertTimeToMinutes = (time: Date): number => {
    return time.getHours() * 60 + time.getMinutes();
};
